import React from 'react';
import Styled from 'styled-components';
import {
  media,
  NavButton,
  ScreenReaderOnly,
  SVGIcon,
  Upload,
} from '@amount/frontend-components';

import { FlexColumn, FlexRow } from '../../Common/CommonStyles';
import { IVerificationsUploadedFile } from '../../../gql';

import { IFileWithPreview } from './common';
import { UploadedFile } from './UploadedFile';

const VerProgressUpload = Styled(FlexColumn)`
  justify-content: space-between;
  &:not(:last-child) {
    margin-bottom: 1em;
  }
`;

const FileDisplay = Styled.div`
  display: flex;
  width: 55%;
  white-space: nowrap;

  ${media.small`
    width: 75%;
  `}
`;

const VerProgressAction = Styled.a<{ legacyUploadTask?: boolean }>`
  padding-left: 1.75em;
  &:first-child {
    padding: 0;
    color: ${({ theme: { brand }, legacyUploadTask }) => (
      !legacyUploadTask && brand.colorAlert
    )};
  }
  &:last-child {
    color: ${({ theme: { brand }, legacyUploadTask }) => (
      legacyUploadTask && brand.colorAlert
    )};
  }
  &:hover {
    color: ${props => props.theme.colorSlate50};
  }
`;

const AddIcon = Styled(SVGIcon)`
  display: flex;
  align-items: center;
  margin-right: 0.5em;

  svg {
    width: 10px;
    height: 10px;
    fill: currentColor;
  }
`;

const FileActionsWrapper = Styled(FlexRow)`
  align-items: flex-end;
  justify-content: flex-end;
`;

const FileInformation = Styled(FlexRow)`
  align-items: flex-end;
  justify-content: space-between;
`;

const AddIconButton = Styled(NavButton)`
  display: flex;
  align-items: center;
`;

const ImagePreview = Styled.img`
  padding: 0.5em 0em;
  width: 234px;
`;

export const FileListDropEnabledUpload = Styled(Upload)`
  display: contents;
`;

const DownloadAnchor: React.FunctionComponent<React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  legacyUploadTask: boolean;
}> = props => {
  const handleClick: React.MouseEventHandler<HTMLAnchorElement> = async e => {
    if (window.navigator && window.navigator.msSaveBlob) {
      const blob = await fetch(props.href as RequestInfo)
        .then(async res => res.blob());
      window.navigator.msSaveBlob(blob, props['data-name']);
    }

    if (props.onClick) {
      props.onClick(e);
    }
  };

  return (
    <VerProgressAction
      {...props}
      onClick={handleClick}
    />
  );
};

interface IDownloadAnchorComponent {
  legacyUploadTask: boolean;
  filename: string;
  url: string | undefined;
  index: number;
}

const DownloadAnchorComponent: React.FC<IDownloadAnchorComponent> = (
  { legacyUploadTask, filename, url, index }
) => (
  <DownloadAnchor
    data-name={filename}
    href={url}
    rel='noopener noreferrer'
    target='_blank'
    id={`view${index}`}
    aria-labelledby={`view${index} file${index}`}
    data-event={`view-${index}`}
    legacyUploadTask={legacyUploadTask}
  >
    View
    <ScreenReaderOnly>File opens in a new window</ScreenReaderOnly>
  </DownloadAnchor>
);

interface IVerProgressActionComponent {
  filename: string;
  index: number;
  legacyUploadTask: boolean;
  removeImage (event: React.MouseEvent<HTMLElement, MouseEvent>): void;
}

const VerProgressActionComponent: React.FC<IVerProgressActionComponent> = (
  { filename, index, removeImage, legacyUploadTask }
) => (
  <VerProgressAction
    as='button'
    data-name={filename}
    onClick={removeImage}
    id={`delete${index}`}
    aria-labelledby={`delete${index} file${index}`}
    data-event={`delete-${index}`}
    legacyUploadTask={legacyUploadTask}
  >
    Delete
  </VerProgressAction>
);

interface IFileList {
  legacyUploadTask: boolean;
  files: Map<string, IFileWithPreview>;
  uploadedFiles?: IVerificationsUploadedFile[];
  removeImage: React.MouseEventHandler<HTMLElement>;
  openFileDialog: React.MouseEventHandler<HTMLElement>;
  hideAddAnotherButton?: boolean;
  showImagePreview?: boolean;
}

class FileList extends React.PureComponent<IFileList> {
  public render () {
    const { legacyUploadTask, files, removeImage, uploadedFiles } = this.props;

    return (
      <>
        {uploadedFiles?.map(({ filename, secureUrl }, index) => (
          <VerProgressUpload key={filename}>
            <FileInformation>
              <FileDisplay id={`uploadedFile${index}`}>
                <UploadedFile filename={filename} />
              </FileDisplay>
              <DownloadAnchorComponent
                filename={filename}
                url={secureUrl}
                index={index}
                legacyUploadTask={legacyUploadTask}
              />
            </FileInformation>
          </VerProgressUpload>
        ))}
        {[...files.values()].map(({ name, preview }, index) => (
          <VerProgressUpload key={name}>
            {
              this.props.showImagePreview && (
                <FlexColumn>
                  <ImagePreview src={preview} />
                </FlexColumn>
              )
            }
            <FileInformation>
              <FileDisplay id={`file${index}`}>
                <UploadedFile filename={name} />
              </FileDisplay>
              <FileActionsWrapper>
                {
                  legacyUploadTask ? (
                    <>
                      <DownloadAnchorComponent
                        filename={name}
                        url={preview}
                        index={uploadedFiles?.length ? index + uploadedFiles.length : index}
                        legacyUploadTask={legacyUploadTask}
                      />
                      <VerProgressActionComponent
                        filename={name}
                        index={uploadedFiles?.length ? index + uploadedFiles.length : index}
                        removeImage={removeImage}
                        legacyUploadTask={legacyUploadTask}
                      />
                    </>
                  ) : (
                    <>
                      <VerProgressActionComponent
                        filename={name}
                        index={uploadedFiles?.length ? index + uploadedFiles.length : index}
                        removeImage={removeImage}
                        legacyUploadTask={legacyUploadTask}
                      />
                      <DownloadAnchorComponent
                        filename={name}
                        url={preview}
                        index={uploadedFiles?.length ? index + uploadedFiles.length : index}
                        legacyUploadTask={legacyUploadTask}
                      />
                    </>
                  )
                }
              </FileActionsWrapper>
            </FileInformation>
          </VerProgressUpload>
        ))}
        {
          !this.props.hideAddAnotherButton && (
            <AddIconButton onClick={this.props.openFileDialog} data-event='addAnotherFile'>
              <AddIcon icon='plus' />
              <span>Add another file</span>
            </AddIconButton>
          )
        }
      </>
    );
  }
}

export default FileList;
